import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import "./PaginaInicio.css";
import "./HomeworkPage.css";
import "./TeacherContent.css";
import Cabecera from "../Others/Cabecera";
import BarraSuperior from "../Others/BarraSuperior";
import BarraInferior from "../Others/BarraInferior";
import PiePagina from "../Others/PiePagina";
import { useData } from "./DataContext";
import {
  subirArchivo,
  obtenerArchivosPorMatricula,
  descargarArchivo,
  eliminarArchivo,
} from "./constants";

// Both roles use the same upload endpoint and supported file extensions.
const MAX_BYTES = 50 * 1024 * 1024;
const ALLOWED_EXTS = ["pdf", "pptx", "xlsx", "docx", "png", "jpg", "jpeg", "mp3", "mp4", "txt"];
const EMPTY_MESSAGE = { type: "", text: "" };

const getExtension = (name) => {
  const dot = (name || "").lastIndexOf(".");
  return dot < 0 ? "" : name.slice(dot + 1).toLowerCase();
};

const formatBytes = (bytes) => {
  if (typeof bytes !== "number" || !Number.isFinite(bytes)) return "-";
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)) - 1, units.length - 1);
  return `${(bytes / Math.pow(1024, index + 1)).toFixed(2)} ${units[index]}`;
};

const errorMessage = (error) => {
  const data = error?.response?.data;
  const message = (typeof data === "string" && data) ||
    data?.message || data?.mensaje ||
    (typeof data?.error === "string" && data.error) ||
    data?.error?.message;
  if (message) return message;
  if (error?.response?.status === 413) return "El archivo supera el tamaño permitido por el servidor.";
  if (error?.response?.status === 415) return "Tipo de archivo no permitido por el servidor.";
  if (error?.response?.status === 401 || error?.response?.status === 403) {
    return "No tienes permisos para realizar esta acción.";
  }
  if (error?.response?.status) return `Error ${error.response.status} al procesar la solicitud.`;
  if (error?.code === "ECONNABORTED") return "La solicitud tardó demasiado. Intenta nuevamente.";
  if (error?.request) return "No hubo respuesta del servidor. Revisa tu conexión.";
  return error?.message || "No se pudo completar la operación.";
};

export const CourseContentManager = () => {
  const { dataClase } = useData();
  const idClase = dataClase?.id_clase;
  const matriculaClase = dataClase?.matricula_clase;
  const [archivo, setArchivo] = useState(null);
  const [archivos, setArchivos] = useState([]);
  const [msg, setMsg] = useState(EMPTY_MESSAGE);
  const [listMsg, setListMsg] = useState(EMPTY_MESSAGE);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const inputRef = useRef(null);
  const requestIdRef = useRef(0);
  const activeClassRef = useRef(matriculaClase);
  activeClassRef.current = matriculaClase;

  const obtenerContenido = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!matriculaClase) {
      setArchivos([]);
      setListMsg({ type: "error", text: "Selecciona una clase para administrar su contenido." });
      return;
    }

    setListMsg({ type: "info", text: "Cargando contenido..." });
    try {
      const response = await axios.get(obtenerArchivosPorMatricula(matriculaClase));
      if (requestId !== requestIdRef.current) return;
      setArchivos(Array.isArray(response.data) ? response.data : []);
      setListMsg(EMPTY_MESSAGE);
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error al obtener contenido:", error);
      setArchivos([]);
      setListMsg({ type: "error", text: "Error al obtener el contenido de esta clase." });
    }
  }, [matriculaClase]);

  useEffect(() => {
    setArchivo(null);
    setMsg(EMPTY_MESSAGE);
    setProgress(0);
    setHighlightId(null);
    setArchivos([]);
    if (inputRef.current) inputRef.current.value = "";
    obtenerContenido();
    return () => {
      requestIdRef.current += 1;
    };
  }, [obtenerContenido]);

  const handleArchivoChange = (event) => {
    const file = event.target.files?.[0];
    setArchivo(null);
    setMsg(EMPTY_MESSAGE);
    setProgress(0);
    if (!file) return;

    const ext = getExtension(file.name);
    if (!ALLOWED_EXTS.includes(ext)) {
      setMsg({
        type: "error",
        text: `Tipo de archivo no permitido: .${ext || "sin extensión"}. Permitidos: ${ALLOWED_EXTS.join(", ")}.`,
      });
      event.target.value = "";
      return;
    }

    if (file.size > MAX_BYTES) {
      setMsg({
        type: "error",
        text: `El archivo supera el límite de ${formatBytes(MAX_BYTES)} (tamaño: ${formatBytes(file.size)}).`,
      });
      event.target.value = "";
      return;
    }
    setArchivo(file);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isUploading) return;
    if (!archivo || !idClase || !matriculaClase) {
      setMsg({ type: "error", text: "Selecciona un archivo y un curso válido." });
      return;
    }

    const claseEnviada = matriculaClase;
    const tipoArchivo = getExtension(archivo.name);
    const formData = new FormData();
    formData.append("archivo", archivo);
    formData.append("id_clase", idClase);
    formData.append("matricula_clase", claseEnviada);
    formData.append("tipo_archivo", tipoArchivo);

    setIsUploading(true);
    setMsg(EMPTY_MESSAGE);
    setProgress(0);
    try {
      const response = await axios.post(subirArchivo, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
        onUploadProgress: (eventProgress) => {
          if (eventProgress.total && activeClassRef.current === claseEnviada) {
            setProgress(Math.round((eventProgress.loaded * 100) / eventProgress.total));
          }
        },
      });

      if (response.data?.ok === false || response.data?.success === false) {
        throw new Error(response.data?.message || "No se pudo subir el archivo.");
      }
      if (activeClassRef.current !== claseEnviada) return;
      setMsg({
        type: "success",
        text: response.data?.message || "Archivo subido correctamente.",
      });
      setArchivo(null);
      if (inputRef.current) inputRef.current.value = "";
      await obtenerContenido();
      if (activeClassRef.current === claseEnviada) {
        setHighlightId(response.data?.id_archivo || null);
      }
    } catch (error) {
      console.error("Error al subir contenido:", error);
      if (activeClassRef.current === claseEnviada) {
        setMsg({ type: "error", text: errorMessage(error) });
      }
    } finally {
      setIsUploading(false);
      setProgress(0);
    }
  };

  const descargarContenido = async (archivoActual) => {
    setBusyId(archivoActual.id_archivo);
    setListMsg(EMPTY_MESSAGE);
    try {
      const response = await axios.get(descargarArchivo(archivoActual.id_archivo), {
        responseType: "blob",
      });
      if (response.headers?.["content-type"]?.includes("application/json")) {
        const body = JSON.parse(await response.data.text());
        throw new Error(body.message || "El servidor no devolvió el archivo.");
      }
      const objectUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = archivoActual.nombre_archivo || "archivo";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error al descargar contenido:", error);
      setListMsg({ type: "error", text: errorMessage(error) });
    } finally {
      setBusyId(null);
    }
  };

  const eliminarContenido = async (archivoActual) => {
    if (!window.confirm(`¿Eliminar "${archivoActual.nombre_archivo}"?`)) return;
    setBusyId(archivoActual.id_archivo);
    setListMsg(EMPTY_MESSAGE);
    try {
      const response = await axios.delete(eliminarArchivo, {
        data: { id_archivo: archivoActual.id_archivo },
        headers: { "Content-Type": "application/json" },
      });
      if (response.data?.success === false) {
        throw new Error(response.data?.message || "No se pudo eliminar el archivo.");
      }
      setArchivos((current) => current.filter((item) => item.id_archivo !== archivoActual.id_archivo));
      setListMsg({ type: "success", text: "Archivo eliminado correctamente." });
    } catch (error) {
      console.error("Error al eliminar contenido:", error);
      setListMsg({ type: "error", text: errorMessage(error) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="contenedor-pagina">
      <Cabecera
        contenidosuperior={<BarraSuperior texto_cabecera={dataClase?.nombre_clase || "Contenido"} />}
        contenidoInferior={<BarraInferior />}
      />

      <main className="homework-container">
        <div className="h-holder">
          <h2>Subir contenido</h2>
          <form className="formulario-subida" onSubmit={handleSubmit}>
            <div className="campo">
              <label htmlFor="archivo">Seleccionar archivo</label>
              <input
                ref={inputRef}
                id="archivo"
                type="file"
                accept={ALLOWED_EXTS.map((ext) => "." + ext).join(",")}
                onChange={handleArchivoChange}
                disabled={isUploading || !idClase || !matriculaClase}
              />
            </div>

            {archivo && (
              <div className="archivo-preview">
                <p><strong>Archivo:</strong> {archivo.name}</p>
                <p><strong>Tamaño:</strong> {formatBytes(archivo.size)}</p>
                <p><strong>Tipo:</strong> {getExtension(archivo.name).toUpperCase()}</p>
              </div>
            )}

            {isUploading && (
              <div className="progreso" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow={progress}>
                <div className="barra" style={{ width: `${progress}%` }} />
                <div className="etiqueta">{progress}%</div>
              </div>
            )}

            <button className="boton-subir" type="submit" disabled={!archivo || isUploading || !idClase || !matriculaClase}>
              {isUploading ? "Subiendo…" : "Subir archivo"}
            </button>
          </form>

          {msg.text && (
            <p role="alert" className={`mensaje mensaje-${msg.type}`}>{msg.text}</p>
          )}

          <h3>Archivos subidos</h3>
          <table className="tabla-archivos">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Tamaño</th>
                <th>Descargar</th>
                <th>Eliminar</th>
              </tr>
            </thead>
            <tbody>
              {archivos.map((item) => (
                <tr key={item.id_archivo} className={item.id_archivo === highlightId ? "fila-resaltada" : ""}>
                  <td>{item.nombre_archivo}</td>
                  <td>{(item.tipo_archivo || getExtension(item.nombre_archivo)).toUpperCase()}</td>
                  <td>{formatBytes(item.tamano_bytes)}</td>
                  <td>
                    <button
                      type="button"
                      className="boton-descargar"
                      onClick={() => descargarContenido(item)}
                      disabled={busyId !== null}
                    >
                      Descargar
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="boton-eliminar"
                      onClick={() => eliminarContenido(item)}
                      disabled={busyId !== null}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              ))}
              {archivos.length === 0 && listMsg.type !== "info" && (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", opacity: 0.7 }}>
                    No hay archivos cargados para esta clase.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {listMsg.text && (
            <p className={`mensaje mensaje-${listMsg.type}`} role="status">{listMsg.text}</p>
          )}
        </div>
      </main>

      <PiePagina />
    </div>
  );
};
